// Reminders have their own server snapshot, independent of the visible inbox.
// Recheck the server at the deadline so cancelled/reassigned reminders stay silent.
export const startWhatsAppReminders = ({ authFetch, agentId, notify, onDue }) => {
    const storageKey = `cti_whatsapp_reminders_notified_${agentId}`;
    const readNotified = () => {
        try {
            const value = JSON.parse(localStorage.getItem(storageKey));
            return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
        } catch {
            return {};
        }
    };
    let notified = readNotified();
    let stopped = false;
    let inFlight = false;
    let refreshPending = false;
    const timers = new Map();
    const controller = new AbortController();

    const refresh = async () => {
        if (stopped) return;
        if (inFlight) {
            refreshPending = true;
            return;
        }
        inFlight = true;
        try {
            const response = await authFetch('/api/whatsapp/reminders', { signal: controller.signal });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            if (stopped || !Array.isArray(data.reminders)) return;

            timers.forEach(clearTimeout);
            timers.clear();
            notified = { ...notified, ...readNotified() };
            const serverNow = Date.parse(data.server_now);
            const now = Number.isFinite(serverNow) ? serverNow : Date.now();
            data.reminders.forEach(reminder => {
                const deadline = Date.parse(reminder.defer_until);
                if (!Number.isFinite(deadline)) return;
                const id = reminder.conversation_id;
                if (deadline <= now) {
                    onDue(reminder);
                    if (notified[id] === reminder.defer_until) return;
                    // Retry on the next check if desktop notifications are blocked.
                    if (notify(reminder)) {
                        notified[id] = reminder.defer_until;
                        try {
                            localStorage.setItem(storageKey, JSON.stringify(notified));
                        } catch { /* Keep deduplication in memory if storage is unavailable. */ }
                    }
                } else {
                    timers.set(id, setTimeout(refresh, Math.min(deadline - now, 2147483647)));
                }
            });
        } catch (error) {
            if (!stopped) console.warn('[WA Reminders] Could not refresh reminders:', error);
        } finally {
            inFlight = false;
            if (refreshPending && !stopped) {
                refreshPending = false;
                refresh();
            }
        }
    };
    const onVisible = () => {
        if (document.visibilityState !== 'hidden') refresh();
    };
    const interval = setInterval(refresh, 60000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    refresh();

    return {
        refresh,
        stop: () => {
            stopped = true;
            controller.abort();
            clearInterval(interval);
            timers.forEach(clearTimeout);
            timers.clear();
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', onVisible);
        },
    };
};
